// Duplicate Prevention Utilities
// Implements client-side request deduplication and request queuing with conflict resolution

interface PendingRequest {
  id: string;
  manifestId: string;
  userId: string;
  timestamp: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  retryCount: number;
  version?: number;
}

interface QueuedRequest {
  id: string;
  manifestId: string;
  userId: string;
  timestamp: number;
  requestData: any;
  retryCount: number;
  clientVersion?: number;
  conflictStrategy?: 'prompt' | 'overwrite' | 'discard';
}

interface ConflictResolution {
  action: 'overwrite' | 'discard' | 'review';
  userConfirmed: boolean;
}

const STORAGE_KEYS = {
  PENDING_REQUESTS: 'ftl_pending_manifest_requests',
  REQUEST_QUEUE: 'ftl_manifest_request_queue',
  COMPLETED_REQUESTS: 'ftl_completed_manifest_requests'
};

const MAX_RETRY_COUNT = 3;
const REQUEST_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const DUPLICATE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Generate a unique request ID based on manifest, user, and operation
 */
export function generateRequestId(manifestId: string, userId: string, operation: string = 'submit'): string {
  return `${operation}_${manifestId}_${userId}_${Date.now()}`;
}

/**
 * Check if a similar request is already pending or recently completed
 */
export function isDuplicateRequest(manifestId: string, userId: string): boolean {
  try {
    // Check pending requests
    const pendingRequests = getPendingRequests();
    const hasPendingRequest = pendingRequests.some(
      (req) =>
        req.manifestId === manifestId &&
        req.userId === userId &&
        req.status === 'PROCESSING' &&
        Date.now() - req.timestamp < REQUEST_TIMEOUT_MS
    );

    if (hasPendingRequest) {
      console.log(
        `Duplicate request detected for manifest ${manifestId} by user ${userId} - request is still processing`
      );
      return true;
    }

    // Check recently completed requests
    const completedRequests = getCompletedRequests();
    const hasRecentCompletion = completedRequests.some(
      (req) =>
        req.manifestId === manifestId && req.userId === userId && Date.now() - req.timestamp < DUPLICATE_WINDOW_MS
    );

    if (hasRecentCompletion) {
      console.log(`Duplicate request detected for manifest ${manifestId} by user ${userId} - recently completed`);
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking for duplicate requests:', error);
    return false; // Allow request if we can't check
  }
}

/**
 * Register a new pending request
 */
export function registerPendingRequest(manifestId: string, userId: string): string {
  try {
    const requestId = generateRequestId(manifestId, userId);
    const pendingRequest: PendingRequest = {
      id: requestId,
      manifestId,
      userId,
      timestamp: Date.now(),
      status: 'PENDING',
      retryCount: 0
    };

    const pendingRequests = getPendingRequests();
    pendingRequests.push(pendingRequest);

    localStorage.setItem(STORAGE_KEYS.PENDING_REQUESTS, JSON.stringify(pendingRequests));
    console.log(`Registered pending request ${requestId} for manifest ${manifestId}`);

    return requestId;
  } catch (error) {
    console.error('Error registering pending request:', error);
    return generateRequestId(manifestId, userId); // Return ID even if storage fails
  }
}

/**
 * Update request status
 */
export function updateRequestStatus(requestId: string, status: PendingRequest['status']): void {
  try {
    const pendingRequests = getPendingRequests();
    const requestIndex = pendingRequests.findIndex((req) => req.id === requestId);

    if (requestIndex !== -1) {
      pendingRequests[requestIndex].status = status;
      pendingRequests[requestIndex].timestamp = Date.now();

      if (status === 'COMPLETED') {
        // Move to completed requests
        const completedRequest = pendingRequests[requestIndex];
        addCompletedRequest(completedRequest);

        // Remove from pending
        pendingRequests.splice(requestIndex, 1);
      }

      localStorage.setItem(STORAGE_KEYS.PENDING_REQUESTS, JSON.stringify(pendingRequests));
      console.log(`Updated request ${requestId} status to ${status}`);
    }
  } catch (error) {
    console.error('Error updating request status:', error);
  }
}

/**
 * Add request to offline queue
 */
export function queueOfflineRequest(manifestId: string, userId: string, requestData: any): string {
  try {
    const requestId = generateRequestId(manifestId, userId, 'queued');
    const queuedRequest: QueuedRequest = {
      id: requestId,
      manifestId,
      userId,
      timestamp: Date.now(),
      requestData,
      retryCount: 0
    };

    const queue = getRequestQueue();
    queue.push(queuedRequest);

    localStorage.setItem(STORAGE_KEYS.REQUEST_QUEUE, JSON.stringify(queue));
    console.log(`Queued offline request ${requestId} for manifest ${manifestId}`);

    return requestId;
  } catch (error) {
    console.error('Error queuing offline request:', error);
    return generateRequestId(manifestId, userId, 'queued');
  }
}

/**
 * Process queued requests when coming back online
 */
export async function processQueuedRequests(
  processFunction: (requestData: any) => Promise<{ success: boolean; error?: string }>
): Promise<void> {
  try {
    const queue = getRequestQueue();
    if (queue.length === 0) return;

    console.log(`Processing ${queue.length} queued requests`);

    for (let i = queue.length - 1; i >= 0; i--) {
      const request = queue[i];

      try {
        // Check if this request would be a duplicate
        if (isDuplicateRequest(request.manifestId, request.userId)) {
          console.log(`Skipping queued request ${request.id} - duplicate detected`);
          queue.splice(i, 1);
          continue;
        }

        // Register as pending before processing
        const pendingId = registerPendingRequest(request.manifestId, request.userId);
        updateRequestStatus(pendingId, 'PROCESSING');

        // Process the request
        const result = await processFunction(request.requestData);

        if (result.success) {
          updateRequestStatus(pendingId, 'COMPLETED');
          queue.splice(i, 1); // Remove from queue
          console.log(`Successfully processed queued request ${request.id}`);
        } else {
          request.retryCount++;
          if (request.retryCount >= MAX_RETRY_COUNT) {
            updateRequestStatus(pendingId, 'FAILED');
            queue.splice(i, 1); // Remove failed request
            console.error(
              `Failed to process queued request ${request.id} after ${MAX_RETRY_COUNT} attempts:`,
              result.error
            );
          } else {
            updateRequestStatus(pendingId, 'FAILED');
            console.warn(`Retrying queued request ${request.id} (attempt ${request.retryCount})`);
          }
        }
      } catch (error) {
        console.error(`Error processing queued request ${request.id}:`, error);
        request.retryCount++;
        if (request.retryCount >= MAX_RETRY_COUNT) {
          queue.splice(i, 1);
        }
      }
    }

    // Update queue in storage
    localStorage.setItem(STORAGE_KEYS.REQUEST_QUEUE, JSON.stringify(queue));
  } catch (error) {
    console.error('Error processing queued requests:', error);
  }
}

/**
 * Clean up old requests from storage
 */
export function cleanupOldRequests(): void {
  try {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours

    // Clean pending requests
    const pendingRequests = getPendingRequests().filter((req) => now - req.timestamp < maxAge);
    localStorage.setItem(STORAGE_KEYS.PENDING_REQUESTS, JSON.stringify(pendingRequests));

    // Clean completed requests
    const completedRequests = getCompletedRequests().filter((req) => now - req.timestamp < maxAge);
    localStorage.setItem(STORAGE_KEYS.COMPLETED_REQUESTS, JSON.stringify(completedRequests));

    // Clean queue (keep failed requests for retry)
    const queue = getRequestQueue().filter((req) => now - req.timestamp < maxAge);
    localStorage.setItem(STORAGE_KEYS.REQUEST_QUEUE, JSON.stringify(queue));

    console.log('Cleaned up old requests from storage');
  } catch (error) {
    console.error('Error cleaning up old requests:', error);
  }
}

/**
 * Get pending requests from storage
 */
function getPendingRequests(): PendingRequest[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PENDING_REQUESTS);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting pending requests:', error);
    return [];
  }
}

/**
 * Get completed requests from storage
 */
function getCompletedRequests(): PendingRequest[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.COMPLETED_REQUESTS);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting completed requests:', error);
    return [];
  }
}

/**
 * Add completed request to storage
 */
function addCompletedRequest(request: PendingRequest): void {
  try {
    const completedRequests = getCompletedRequests();
    completedRequests.push(request);

    // Keep only recent completed requests (last 50)
    if (completedRequests.length > 50) {
      completedRequests.splice(0, completedRequests.length - 50);
    }

    localStorage.setItem(STORAGE_KEYS.COMPLETED_REQUESTS, JSON.stringify(completedRequests));
  } catch (error) {
    console.error('Error adding completed request:', error);
  }
}

/**
 * Get request queue from storage
 */
function getRequestQueue(): QueuedRequest[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.REQUEST_QUEUE);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting request queue:', error);
    return [];
  }
}

/**
 * Check for conflicts with the server before processing a request
 */
export async function checkForConflicts(
  manifestId: string,
  userId: string,
  clientVersion?: number
): Promise<{ conflict: boolean; data?: any; error?: string }> {
  try {
    // Get API URL from AWS configuration
    const awsconfig = await import('../../aws-exports');
    const apiUrl = (awsconfig.default as any).aws_conflict_api_endpoint;

    if (!apiUrl || apiUrl === 'PLACEHOLDER_CONFLICT_API_URL') {
      return {
        conflict: false,
        error: 'Conflict API not configured - proceeding without conflict detection'
      };
    }

    const response = await fetch(`${apiUrl}/conflicts/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        manifestId,
        userId,
        clientVersion,
        operation: 'manifest-submit'
      })
    });

    const result = await response.json();

    if (response.status === 409) {
      // Conflict detected
      return {
        conflict: true,
        data: result
      };
    } else if (response.ok) {
      // No conflict
      return {
        conflict: false,
        data: result
      };
    } else {
      // API error
      return {
        conflict: false,
        error: result.error || 'Failed to check for conflicts'
      };
    }
  } catch (error) {
    console.error('Error checking for conflicts:', error);
    return {
      conflict: false,
      error: 'Network error while checking for conflicts'
    };
  }
}

/**
 * Enhanced queue function with conflict detection
 */
export function queueOfflineRequestWithVersion(
  manifestId: string,
  userId: string,
  requestData: any,
  clientVersion?: number,
  conflictStrategy: 'prompt' | 'overwrite' | 'discard' = 'prompt'
): string {
  try {
    const requestId = generateRequestId(manifestId, userId, 'queued');
    const queuedRequest: QueuedRequest = {
      id: requestId,
      manifestId,
      userId,
      timestamp: Date.now(),
      requestData,
      retryCount: 0,
      clientVersion,
      conflictStrategy
    };

    const queue = getRequestQueue();
    queue.push(queuedRequest);

    localStorage.setItem(STORAGE_KEYS.REQUEST_QUEUE, JSON.stringify(queue));
    console.log(`Queued offline request ${requestId} for manifest ${manifestId} with version ${clientVersion}`);

    return requestId;
  } catch (error) {
    console.error('Error queuing offline request with version:', error);
    return generateRequestId(manifestId, userId, 'queued');
  }
}

/**
 * Enhanced process function with conflict resolution
 */
export async function processQueuedRequestsWithConflictResolution(
  processFunction: (requestData: any) => Promise<{ success: boolean; error?: string }>,
  conflictResolver?: (conflictData: any) => Promise<ConflictResolution>
): Promise<void> {
  try {
    const queue = getRequestQueue();
    if (queue.length === 0) return;

    console.log(`Processing ${queue.length} queued requests with conflict detection`);

    for (let i = queue.length - 1; i >= 0; i--) {
      const request = queue[i];

      try {
        // Check if this request would be a duplicate
        if (isDuplicateRequest(request.manifestId, request.userId)) {
          console.log(`Skipping queued request ${request.id} - duplicate detected`);
          queue.splice(i, 1);
          continue;
        }

        // Check for conflicts before processing
        const conflictCheck = await checkForConflicts(request.manifestId, request.userId, request.clientVersion);

        if (conflictCheck.error) {
          console.warn(`Conflict check failed for ${request.id}: ${conflictCheck.error}`);
          // Continue with processing if conflict check fails
        } else if (conflictCheck.conflict) {
          console.log(`Conflict detected for queued request ${request.id}`);

          // Handle conflict based on strategy
          if (request.conflictStrategy === 'discard') {
            console.log(`Discarding conflicted request ${request.id} as per strategy`);
            queue.splice(i, 1);
            continue;
          } else if (request.conflictStrategy === 'overwrite') {
            console.log(`Overwriting conflicted request ${request.id} as per strategy`);
            // Continue with processing
          } else if (request.conflictStrategy === 'prompt' && conflictResolver) {
            // Ask user for resolution
            try {
              const resolution = await conflictResolver(conflictCheck.data);

              if (resolution.action === 'discard') {
                console.log(`User chose to discard conflicted request ${request.id}`);
                queue.splice(i, 1);
                continue;
              } else if (resolution.action === 'review') {
                console.log(`User chose to review conflicted request ${request.id} - skipping for now`);
                continue; // Keep in queue for manual review
              }
              // If 'overwrite', continue with processing
            } catch (resolverError) {
              console.error(`Conflict resolver failed for ${request.id}:`, resolverError);
              continue; // Skip this request
            }
          } else {
            // No resolver provided, skip conflicted requests
            console.log(`No conflict resolver provided for ${request.id} - skipping`);
            continue;
          }
        }

        // Register as pending before processing
        const pendingId = registerPendingRequest(request.manifestId, request.userId);
        updateRequestStatus(pendingId, 'PROCESSING');

        // Process the request
        const result = await processFunction(request.requestData);

        if (result.success) {
          updateRequestStatus(pendingId, 'COMPLETED');
          queue.splice(i, 1); // Remove from queue
          console.log(`Successfully processed queued request ${request.id}`);
        } else {
          request.retryCount++;
          if (request.retryCount >= MAX_RETRY_COUNT) {
            updateRequestStatus(pendingId, 'FAILED');
            queue.splice(i, 1); // Remove failed request
            console.error(
              `Failed to process queued request ${request.id} after ${MAX_RETRY_COUNT} attempts:`,
              result.error
            );
          } else {
            updateRequestStatus(pendingId, 'FAILED');
            console.warn(`Retrying queued request ${request.id} (attempt ${request.retryCount})`);
          }
        }
      } catch (error) {
        console.error(`Error processing queued request ${request.id}:`, error);
        request.retryCount++;
        if (request.retryCount >= MAX_RETRY_COUNT) {
          queue.splice(i, 1);
        }
      }
    }

    // Update queue in storage
    localStorage.setItem(STORAGE_KEYS.REQUEST_QUEUE, JSON.stringify(queue));
  } catch (error) {
    console.error('Error processing queued requests with conflict resolution:', error);
  }
}

/**
 * Get queue status for debugging
 */
export function getQueueStatus(): {
  pendingCount: number;
  queuedCount: number;
  completedCount: number;
} {
  return {
    pendingCount: getPendingRequests().length,
    queuedCount: getRequestQueue().length,
    completedCount: getCompletedRequests().length
  };
}
